import { useState, useEffect, useRef } from "react";
import "../style/MultiStackScroll.css";
import Fram1 from "../assets/stack/customer_list.jpeg";
import Fram2 from "../assets/stack/employee_list.jpeg";
import Fram3 from "../assets/stack/jobs_list.jpeg";
import Fram4 from "../assets/stack/calendar.jpeg";
import Fram5 from "../assets/stack/myjobs_emp.jpeg";
import Fram6 from "../assets/stack/forecast.jpeg";
import Fram7 from "../assets/stack/debt_list.jpeg";
import Fram8 from "../assets/stack/notifications.jpeg";
import Fram9 from "../assets/stack/Frame9.png";
import Template from "../assets/stack/TaskTemplates.jpeg"

const pages = [
  {
    id: 0,
    title: "Customers",
    subtitle: "Create, Store & Manage Every Customer Effortlessly",
    bullets: [
      "Add, edit & archive customers",
      "Quick customer search",
      "Categorise customers (All / Active / Archived)",
      "Store full contact details",
      "Control VAT status per customer",
    ],
    image: Fram1,
  },
  {
    id: 1,
    title: "Employees",
    subtitle: "Manage Your Team & Their Workflows Efficiently",
    bullets: [
      "Add, edit & remove employees",
      "Restrict access to customer data",
      "View jobs assigned to each employee",
      "Allow employees to mark jobs as complete",
      "Track employee job activity and status",
    ],
    image: Fram2,
  },
  {
    id: 2,
    title: "Jobs",
    subtitle: "Create, Schedule & Manage Every Job With Ease",
    bullets: [
      "Create one-off or recurring jobs (dailly, weekly, monthly, etc.)",
      "Build and use task templates to speed up job creation",
      "Assign team members and set dates/times",
      "Manage pricing, payment teerms & payment methods",
      "Send quotation for approval and track job/payment status automatically",
    ],
    image: Fram3,
  },
  {
    id: 3,
    title: "Task Templates",
    subtitle: "Save Time. Stay Consistent. Charge with Confidence.",
    bullets: [
      "Ensures consistent pricing every time and removes guesswork.",
      "Includes professional descriptions so customers know exactly what the job involves.",
      "Reduces admin time and speeds up quoting dramatically.",
      "Makes it easier for teams and apprentices to avoid mistakes or misquotes.",
    ],
    image: Template,
  },
  {
    id: 4,
    title: "My Jobs (Employee View)",
    subtitle: "Your Team Always Knows What To Do Next",
    bullets: [
      "Today's jobs view",
      "Tap to complete",
      "Upload photos",
      "Limited access (privacy protection)",
      "Status sync with owner dashboard",
    ],
    image: Fram5,
  },
  {
    id: 5,
    title: "Calendar & Scheduling",
    subtitle: "Plan Your Days, Weeks & Months In Seconds",
    bullets: [
      "Daily, Weekly, and Monthly views",
      "Drag-and-drop (desktop) & Tap-to-change (mobile)",
      "Recurring job editing",
      "Employee holiday & time-off visibility",
      "Payment colour indicators (green/orange/red)",
    ],
    image: Fram4,
  },
  {
    id: 6,
    title: "Forecasting",
    subtitle: "Know Your Future Revenue Before It Happens",
    bullets: [
      "Year overview",
      "Monthly breakdown",
      "Scheduled vs waiting response",
      "Compare future years",
      "Business planning",
    ],
    image: Fram6,
  },
  {
    id: 7,
    title: "Debt List",
    subtitle: "Never Lose Track Of Money Owed Again",
    bullets: [
      "Outstanding balances",
      "Days overdue (with colour coding)",
      "Per-job breakdown",
      "Export PDF/Excel",
      "Auto-sorted by urgency",
    ],
    image: Fram7,
  },
  {
    id: 8,
    title: "Notification Centre",
    subtitle: "Stay Updated Without Lifting A Finger",
    bullets: [
      "Quote viewed alerts",
      "Quote accepted/declined notifications",
      "Invoice opened tracking",
      "Payment received alerts",
      "Job status changes & Employee completion",
    ],
    image: Fram8,
  },
  {
    id: 9,
    title: "Settings & Personalisation",
    subtitle: "Customise DayDone To Fit Your Business",
    bullets: [
      "Business info & VAT settings",
      "Communication method (SMS/Email/Both)",
      "Bank details",
      "Stripe payout account setup",
      "Notification preferences & Quote Validity",
    ],
    image: Fram9,
  },
];

const FeatureCard = ({ page }) => {
  return (
    <div className="stack-card">
      {/* Image */}
      <div className="stack-image-wrapper">
        <img src={page.image} alt={page.title} className="stack-image" />
      </div>

      {/* Text Content Below Image */}
      <div className="stack-text-content">
        <h3 className="stack-title">{page.title}</h3>
        <p className="stack-subtitle">{page.subtitle}</p>
        <ul className="bullet-list">
          {page.bullets.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MultiStackScroll = () => {
  const scrollContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const cardWidth = container.querySelector('.stack-card')?.offsetWidth || 0;
      const gap = isMobile ? 0 : 50;
      
      // Calculate which card is most visible
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(Math.max(0, index), pages.length - 1));
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial call
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
    
  }, [isMobile]);

  // Magnetic snap on scroll end (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollTimeout;
    
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const cardWidth = container.querySelector('.stack-card')?.offsetWidth || 0;
        const gap = 50;
        const scrollLeft = container.scrollLeft;
        const nearestIndex = Math.round(scrollLeft / (cardWidth + gap));
        
        scrollToCard(nearestIndex);
      }, 150);
    };

    container.addEventListener('scroll', handleScrollEnd);
    
    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener('scroll', handleScrollEnd);
    };
     // eslint-disable-next-line
  }, [isMobile]);

  const scrollToCard = (index) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = container.querySelector('.stack-card')?.offsetWidth || 0;
    const gap = isMobile ? 0 : 50;
    const scrollPosition = index * (cardWidth + gap);
    
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToCard(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < pages.length - 1) {
      scrollToCard(activeIndex + 1);
    }
  };

  return (
    <div className="stack-container" id="features">
      {/* Section Header */}
      <div className="stack-main-header">
        <h2 className="stack-main-title">Inside DayDone's Core Tools</h2>
        <p className="stack-main-subtitle">
          See how customers, employees, jobs, scheduling, payments, and team tools work together to streamline your entire business.
        </p>
      </div>

      {/* Horizontal Scrollable Cards */}
      <div className="scroll-wrapper">
        {/* Previous Button - Desktop Only */}
        {!isMobile && (
          <button
            className={`nav-button nav-button-left ${activeIndex === 0 ? 'disabled' : ''}`}
            onClick={handlePrevious}
            disabled={activeIndex === 0}
            aria-label="Previous card"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <div 
          className="stack-scroll-container"
          ref={scrollContainerRef}
        >
          {pages.map((page) => (
            <FeatureCard key={page.id} page={page} />
          ))}
        </div>

        {/* Next Button - Desktop Only */}
        {!isMobile && (
          <button
            className={`nav-button nav-button-right ${activeIndex === pages.length - 1 ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={activeIndex === pages.length - 1}
            aria-label="Next card"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Scroll Indicators (Bubbles) */}
        <div className="scroll-indicators">
          {pages.map((_, index) => (
            <button
              key={index}
              className={`scroll-bubble ${activeIndex === index ? 'active' : ''}`}
              onClick={() => scrollToCard(index)}
              aria-label={`Scroll to card ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default MultiStackScroll;